import {
  brandingResponseSchema,
  type BrandingResponse,
  type LocalizedList,
  type LocalizedText,
} from '@delegation/contracts';
import { create } from 'zustand';

import { http } from '../api/http';

import { env } from '@/lib/env';

/// The deployment's client identity (name, logo, bank account) comes from the
/// API at boot — one build serves every client. Until it loads, or if the API
/// can't be reached, the UI shows this neutral identity: never another client's.
export const FALLBACK_BRANDING: BrandingResponse = {
  id: 'default',
  name: { en: 'Delegation System', ar: 'نظام التفويض' },
  shortName: { en: 'Delegation', ar: 'التفويض' },
  bankAccount: null,
  form: { services: [], utilitiesBundle: null, entryPermits: null },
  channels: { whatsapp: false, email: false },
  assetsVersion: '0',
};

interface BrandingState {
  branding: BrandingResponse;
}

export const useBrandingStore = create<BrandingState>(() => ({ branding: FALLBACK_BRANDING }));

export const useBranding = (): BrandingResponse => useBrandingStore((s) => s.branding);

/// URL of a brand asset served by the API, cache-busted by the asset hash.
export const brandAssetUrl = (asset: 'logo' | 'icon', branding: BrandingResponse): string =>
  `${env.VITE_API_URL.replace(/\/+$/, '')}/branding/${asset}?v=${encodeURIComponent(branding.assetsVersion)}`;

/// Arabic for an `ar*` language, English otherwise.
export const localized = (text: LocalizedText, language: string | undefined): string =>
  language?.startsWith('ar') ? text.ar : text.en;

/// Same for a bilingual list; an absent list is empty.
export const localizedList = (
  list: LocalizedList | null | undefined,
  language: string | undefined,
): string[] => (list ? (language?.startsWith('ar') ? list.ar : list.en) : []);

/// Fetch the brand into the store. Never throws — a failure keeps the neutral
/// fallback so the app still boots.
export async function loadBranding(): Promise<BrandingResponse> {
  try {
    const raw = await http.get('branding', { timeout: 5_000 }).json();
    const branding = brandingResponseSchema.parse(raw);
    useBrandingStore.setState({ branding });
    return branding;
  } catch (error) {
    console.warn('Branding unavailable, using the neutral fallback:', error);
    return FALLBACK_BRANDING;
  }
}

/// Tab title + favicon for the brand (index.html only carries neutral values).
export function applyBrandingToDocument(branding: BrandingResponse): void {
  document.title = `${branding.name.ar} | ${branding.name.en}`;
  let icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!icon) {
    icon = document.createElement('link');
    icon.rel = 'icon';
    document.head.appendChild(icon);
  }
  icon.removeAttribute('type');
  icon.href = brandAssetUrl('icon', branding);
}
